import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { mutate } from 'swr';
import {
  AlertCode,
  ProcessCheckoutPayload,
  ProcessCheckoutResponse,
  UNKNOWN_PSP,
  ValidateCheckoutResponse,
} from 'shared/types/checkout';
import { withCookieValidation } from 'helpers/utils/cookie';
import { sdk } from 'sdk';
import {
  getAccountId,
  setAlert,
  setPaymentCTAState,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { logout } from 'frontastic/lib/utils/logout';
import {
  getAlertCodeForError,
  getAlertCodeFromError,
  getProcessCheckoutPayloadForPSP,
  getValidateCheckoutPayloadForPSP,
  sendCohesionConversionEvent,
  sendOrderCompletedEvent,
  sendPaymentInfoEnteredEvent,
} from './helpers';
import {
  HandleCheckoutProcessOptions,
  PlaceFreeOrderOptions,
  PlaceOrderOptions,
} from './types';

const THANK_YOU_PAGE_PATH = '/thank-you';

export const useCheckout = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);

  const { push, replace } = useRouter();

  const handleCheckoutProcess = useCallback(
    async ({
      psp,
      cartID,
      priceDetails,
      pspData,
    }: HandleCheckoutProcessOptions) => {
      let response;
      const validateResponse = await withCookieValidation(() =>
        sdk.callAction<ValidateCheckoutResponse>(
          getValidateCheckoutPayloadForPSP({
            psp,
            cartID,
            priceDetails,
            pspData,
          }),
        ),
      );

      if (validateResponse.isError || validateResponse.data.isError) {
        response = validateResponse;
      } else {
        // pspPaymentID will be paypal order id for PayPal PSP and payment intent id for Stripe PSP
        const { ctPaymentID, payerName } = validateResponse.data;
        response = await withCookieValidation(() =>
          sdk.callAction<ProcessCheckoutResponse>(
            getProcessCheckoutPayloadForPSP({
              psp,
              cartID,
              ctPaymentID,
              payerName,
            }),
          ),
        );
      }

      return response.isError
        ? response
        : (response.data as ProcessCheckoutResponse);
    },
    [],
  );

  const placeOrder = useCallback(
    async ({
      cartID,
      lineItems,
      priceDetails,
      paymentMethodType,
      psp,
      pspData,
    }: PlaceOrderOptions) => {
      dispatch(setPaymentCTAState({ psp, value: true }));
      try {
        sendPaymentInfoEnteredEvent({
          cartID,
          lineItems,
          priceDetails,
          paymentMethodType,
        });

        const apiResponse = await handleCheckoutProcess({
          psp,
          cartID,
          priceDetails,
          pspData,
        });

        if (apiResponse.isError) {
          const errorCode = (apiResponse.error?.code as string) ?? '';
          const errorMessage =
            apiResponse.error?.message ??
            JSON.stringify(apiResponse.error) ??
            'unknown';

          // SDN handling
          if (errorCode === 'sdn_matched_error') {
            console.error('SDN matched for customer:', customerId);
            await logout();
            replace(
              process.env.NEXT_PUBLIC_EDX_LEGACY_ECOM_BASE_URL! +
                process.env.NEXT_PUBLIC_EDX_SDN_FAILURE_PAGE_PATH!,
            );
            return;
          }
          dispatch(setPaymentCTAState({ psp, value: false }));

          const alertCode = getAlertCodeForError(apiResponse.error, psp);

          console.error(
            `Error in processCheckout extension for ${psp} for customer: ${customerId} with error code: ${errorCode} and error message: ${errorMessage}`,
          );
          dispatch(setAlert({ code: alertCode }));

          await mutate('/action/cart/getCart');
          sendCohesionConversionEvent([], 'Cart', 'Declined');
        } else {
          const { order } = apiResponse;
          if (!order) {
            throw new Error('No order found in apiResponse from the server');
          }

          sendCohesionConversionEvent(
            order.lineItems,
            'Enrollment',
            'Completed',
          );

          sendOrderCompletedEvent({
            psp,
            cartID,
            priceDetails,
            paymentMethodType,
            order,
          });

          const params = new URLSearchParams({
            order_id: order.orderId,
          }).toString();
          push(`${THANK_YOU_PAGE_PATH}/?${params}`);
        }
      } catch (error) {
        console.error(
          `Order placement error via ${psp} for customer:${customerId}. Error:${
            JSON.stringify(error) ?? 'unknown'
          }`,
        );
        dispatch(setAlert({ code: AlertCode.PAYMENT_PROCESSING_FAILED }));
        dispatch(setPaymentCTAState({ psp, value: false }));
        sendCohesionConversionEvent([], 'Cart', 'Declined');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const placeFreeOrder = useCallback(
    async ({ cartID, lineItems, priceDetails }: PlaceFreeOrderOptions) => {
      dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: true }));
      try {
        sendPaymentInfoEnteredEvent({
          cartID,
          lineItems,
          priceDetails,
          paymentMethodType: 'free',
        });

        const payload = {
          cartID,
        } as ProcessCheckoutPayload;

        const apiResponse = await withCookieValidation(() =>
          sdk.callAction<ProcessCheckoutResponse>({
            actionName: 'order/placeFreeOrder',
            payload,
          }),
        );

        if (apiResponse.isError) {
          const errorCode = (apiResponse.error?.code as string) ?? '';
          const errorMessage =
            apiResponse.error?.message ??
            JSON.stringify(apiResponse.error) ??
            'unknown';

          const alertCode = getAlertCodeFromError(apiResponse.error);

          console.error(
            `Error in processCheckout extension while processing for free order
           for customer: ${customerId} with error code: ${errorCode} and error message: ${errorMessage}`,
          );
          dispatch(setAlert({ code: alertCode }));
          dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));

          await mutate('/action/cart/getCart');
          sendCohesionConversionEvent([], 'Cart', 'Declined');
        } else if (apiResponse.data.isError) {
          const { error } = apiResponse.data;
          const alertCode = getAlertCodeFromError(apiResponse.data.error);
          console.error(
            `Error in processCheckout extension for free order for customer: ${customerId} with error message: ${error?.message ?? (error as unknown as string)}`,
          );
          dispatch(
            setAlert({ code: alertCode ?? AlertCode.ORDER_PROCESSING_FAILED }),
          );
          dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));
          sendCohesionConversionEvent([], 'Cart', 'Declined');
        } else {
          const { order } = apiResponse.data;
          if (!order) {
            throw new Error('No order found in apiResponse from the server');
          }

          sendCohesionConversionEvent(
            order.lineItems,
            'Enrollment',
            'Completed',
          );

          sendOrderCompletedEvent({
            psp: UNKNOWN_PSP.Type,
            cartID,
            priceDetails,
            paymentMethodType: UNKNOWN_PSP.Type,
            order,
          });

          const params = new URLSearchParams({
            order_id: order.orderId,
          }).toString();
          push(`${THANK_YOU_PAGE_PATH}/?${params}`);
        }
      } catch (error) {
        console.error(
          `Order placement error for free for customer:${customerId}. Error:${
            JSON.stringify(error) ?? 'unknown'
          }`,
        );
        dispatch(setPaymentCTAState({ psp: UNKNOWN_PSP.Type, value: false }));
        dispatch(setAlert({ code: AlertCode.ORDER_PROCESSING_FAILED }));
        sendCohesionConversionEvent([], 'Cart', 'Declined');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  return { placeOrder, placeFreeOrder };
};
